/* Progressive enhancements for homepage content. Liquid remains usable without JavaScript. */
(() => {
  if (!customElements.get('zb-content-marquee')) {
    customElements.define('zb-content-marquee', class extends HTMLElement {
      connectedCallback() {
        this.disconnectEnhancement();
        this.track = this.querySelector('[data-zb-marquee-track]');
        this.button = this.querySelector('[data-zb-marquee-toggle]');
        this.viewport = this.querySelector('[data-zb-marquee-viewport]');
        if (!this.track || !this.button || !this.viewport) return;
        this.events = new AbortController();
        this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.manualPause = false;
        const options = { signal: this.events.signal };
        this.button.addEventListener('click', () => {
          this.manualPause = !this.manualPause;
          this.refreshMotion();
        }, options);
        this.addEventListener('mouseenter', () => { this.hovered = true; this.updatePlayState(); }, options);
        this.addEventListener('mouseleave', () => { this.hovered = false; this.updatePlayState(); }, options);
        this.addEventListener('focusin', (event) => {
          this.focused = event.target !== this.button;
          this.updatePlayState();
        }, options);
        this.addEventListener('focusout', () => { this.focused = false; this.updatePlayState(); }, options);
        document.addEventListener('visibilitychange', () => this.updatePlayState(), options);
        this.motionChange = () => this.refreshMotion();
        this.motion.addEventListener('change', this.motionChange);
        this.observer = new ResizeObserver(() => {
          cancelAnimationFrame(this.frame);
          this.frame = requestAnimationFrame(() => this.refreshMotion());
        });
        this.observer.observe(this.viewport);
        this.refreshMotion();
      }

      refreshMotion() {
        this.track.querySelectorAll('[data-zb-marquee-clone]').forEach((item) => item.remove());
        this.classList.remove('zb-marquee--moving');
        this.classList.add('zb-marquee--static');
        this.button.hidden = true;
        if (this.dataset.autoslide !== 'true' || this.motion.matches) return;

        const originals = Array.from(this.track.children);
        if (!originals.length) return;
        this.classList.remove('zb-marquee--static');
        const overflows = this.track.scrollWidth > this.viewport.clientWidth + 1;
        if (!overflows) {
          this.classList.add('zb-marquee--static');
          return;
        }
        this.button.hidden = false;
        this.button.setAttribute('aria-pressed', String(this.manualPause));
        this.button.textContent = this.manualPause ? 'Resume animation' : 'Pause animation';
        if (this.manualPause) {
          this.classList.add('zb-marquee--static');
          return;
        }
        originals.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.dataset.zbMarqueeClone = '';
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('inert', '');
          clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
          clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((node) => node.setAttribute('tabindex', '-1'));
          this.track.appendChild(clone);
        });
        const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
        const distance = originals.reduce((width, item) => width + item.getBoundingClientRect().width + gap, 0);
        this.style.setProperty('--zb-marquee-distance', `${distance}px`);
        this.classList.add('zb-marquee--moving');
        this.updatePlayState();
      }

      updatePlayState() {
        if (this.track) this.track.style.animationPlayState = this.manualPause || this.hovered || this.focused || document.hidden ? 'paused' : 'running';
      }

      disconnectEnhancement() {
        this.events?.abort();
        this.observer?.disconnect();
        cancelAnimationFrame(this.frame);
        if (this.motion && this.motionChange) this.motion.removeEventListener('change', this.motionChange);
        this.track?.querySelectorAll('[data-zb-marquee-clone]').forEach((item) => item.remove());
        this.classList.remove('zb-marquee--moving');
        this.classList.add('zb-marquee--static');
        if (this.button) this.button.hidden = true;
        this.hovered = false;
        this.focused = false;
      }

      disconnectedCallback() { this.disconnectEnhancement(); }
    });
  }

  if (!customElements.get('zb-content-guides')) {
    customElements.define('zb-content-guides', class extends HTMLElement {
      connectedCallback() {
        this.disconnectedCallback();
        this.track = this.querySelector('[data-slider]');
        this.prev = this.querySelector('[data-prev]');
        this.next = this.querySelector('[data-next]');
        this.navigation = this.querySelector('[data-zb-guide-navigation]');
        if (!this.track || !this.prev || !this.next || !this.navigation) return;
        this.events = new AbortController();
        const options = { signal: this.events.signal };
        this.prev.addEventListener('click', () => this.move(-1), options);
        this.next.addEventListener('click', () => this.move(1), options);
        this.track.addEventListener('scroll', () => this.updateControls(), { ...options, passive: true });
        this.observer = new ResizeObserver(() => this.updateControls());
        this.observer.observe(this.track);
        this.updateControls();
      }

      move(direction) {
        const card = this.track.firstElementChild;
        if (!card) return;
        const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: reduced ? 'auto' : 'smooth' });
      }

      updateControls() {
        const distance = this.track.scrollWidth - this.track.clientWidth;
        this.navigation.hidden = distance <= 1;
        this.prev.disabled = this.track.scrollLeft <= 1;
        this.next.disabled = this.track.scrollLeft >= distance - 1;
      }

      disconnectedCallback() {
        this.events?.abort();
        this.observer?.disconnect();
        if (this.navigation) this.navigation.hidden = true;
      }
    });
  }

  if (!customElements.get('zb-content-slider')) {
    customElements.define('zb-content-slider', class extends HTMLElement {
      connectedCallback() {
        this.disconnectedCallback();
        this.track = this.querySelector('[data-zb-slider-track]');
        this.prev = this.querySelector('[data-prev], [data-scroll-prev]');
        this.next = this.querySelector('[data-next], [data-scroll-next]');
        this.navigation = this.querySelector('[data-zb-slider-nav]');
        this.dotsContainer = this.querySelector('[data-dots]');
        this.positionLabel = this.querySelector('[data-zb-slider-status]');
        if (!this.track) return;
        this.cards = Array.from(this.track.children);
        this.events = new AbortController();
        const options = { signal: this.events.signal };
        this.prev?.addEventListener('click', () => this.move(-1), options);
        this.next?.addEventListener('click', () => this.move(1), options);
        this.track.addEventListener('scroll', () => this.updateControls(), { ...options, passive: true });
        if (this.dataset.indicatorStyle === 'numbered') {
          this.requestedIndex = undefined;
          ['pointerdown', 'touchstart', 'wheel'].forEach((type) => {
            this.track.addEventListener(type, () => { this.requestedIndex = undefined; }, { ...options, passive: true });
          });
        }
        this.track.addEventListener('keydown', (event) => {
          if (event.target !== this.track || !this.overflows) return;
          const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
          if (!keys.includes(event.key)) return;
          event.preventDefault();
          if (event.key === 'Home') this.showCard(0);
          else if (event.key === 'End') this.showCard(this.cards.length - 1);
          else this.move(event.key === 'ArrowLeft' ? -1 : 1);
        }, options);
        this.createIndicators();
        this.observer = new ResizeObserver(() => this.updateControls());
        this.observer.observe(this.track);
        this.cards.forEach((card) => this.observer.observe(card));
        this.updateControls();
      }

      createIndicators() {
        if (!this.dotsContainer) return;
        this.dotsContainer.replaceChildren();
        this.cards.forEach((card, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = `zb-slider-dot ${this.dataset.dotClass || ''}`;
          const title = card.querySelector('h3')?.textContent.trim();
          dot.setAttribute('aria-label', title ? `Show ${title}` : `Show slide ${index + 1}`);
          dot.dataset.zbSliderDot = String(index);
          if (this.dataset.indicatorStyle === 'numbered') dot.textContent = String(index + 1).padStart(2, '0');
          dot.addEventListener('click', () => this.showCard(index), { signal: this.events.signal });
          this.dotsContainer.append(dot);
        });
      }

      cardPosition(index) {
        if (!this.cards[index]) return 0;
        return this.cards[index].getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
      }

      showCard(index) {
        if (!this.cards.length || !this.overflows) return;
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        // Several final cards can share the same scroll endpoint on a wide track.
        // Retain the explicitly chosen numbered step once its position is reached.
        if (this.dataset.indicatorStyle === 'numbered') this.requestedIndex = index;
        this.track.scrollTo({ left: this.cardPosition(index), behavior: reduced ? 'auto' : 'smooth' });
        this.updateControls();
      }

      move(direction) {
        if (!this.cards.length || !this.overflows) return;
        this.requestedIndex = undefined;
        const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
        const distance = this.cards[0].getBoundingClientRect().width + gap;
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollBy({ left: direction * distance, behavior: reduced ? 'auto' : 'smooth' });
      }

      updateControls() {
        const distance = this.track.scrollWidth - this.track.clientWidth;
        this.overflows = this.dataset.layout !== 'grid' && distance > 1 && this.cards.length > 1;
        if (this.navigation) this.navigation.hidden = !this.overflows;
        if (this.dotsContainer) this.dotsContainer.hidden = !this.overflows;
        if (this.prev) this.prev.disabled = !this.overflows || this.track.scrollLeft <= 1;
        if (this.next) this.next.disabled = !this.overflows || this.track.scrollLeft >= distance - 1;
        this.track.tabIndex = this.overflows ? 0 : -1;
        this.currentIndex = 0;
        let closest = Infinity;
        this.cards.forEach((card, index) => {
          const separation = Math.abs(this.cardPosition(index) - this.track.scrollLeft);
          if (separation < closest) { closest = separation; this.currentIndex = index; }
        });
        if (this.overflows && this.track.scrollLeft >= distance - 1) this.currentIndex = this.cards.length - 1;
        if (Number.isInteger(this.requestedIndex)) {
          const target = Math.max(0, Math.min(distance, this.cardPosition(this.requestedIndex)));
          if (Math.abs(this.track.scrollLeft - target) <= 2) this.currentIndex = this.requestedIndex;
        }
        if (this.positionLabel) this.positionLabel.textContent = `Step ${String(this.currentIndex + 1).padStart(2, '0')} / ${String(this.cards.length).padStart(2, '0')}`;
        if (this.positionLabel) this.style.setProperty('--zb-slider-progress', `${((this.currentIndex + 1) / this.cards.length) * 100}%`);
        this.dotsContainer?.querySelectorAll('[data-zb-slider-dot]').forEach((dot, index) => {
          const selected = index === this.currentIndex;
          dot.classList.toggle('active', selected);
          if (selected) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      }

      disconnectedCallback() {
        this.events?.abort();
        this.observer?.disconnect();
        if (this.navigation) this.navigation.hidden = true;
        if (this.dotsContainer) { this.dotsContainer.replaceChildren(); this.dotsContainer.hidden = true; }
      }
    });
  }
})();
